import proj4 from 'proj4';
import { LinearFilter, NearestFilter, SRGBColorSpace } from 'three';

import { ProjectGeodataManager } from '../../project-geodata-manager';
import { renderOrders } from '../../render-orders';
import { ViewSettings } from '../../view-settings';
import { createCogOrthoAsset } from '../cog-asset/create-cog-ortho-asset';
import { FlatPlaneWorldMapping } from './flat-plane-world-mapping';
import { makeTileBoundsToWorldQuad } from './make-tile-bounds-to-world-quad';
import { OrthoTileAsset } from './ortho-tile-asset';
import { TileCache } from './tile-cache';
import { TileLoader } from './tile-loader';

/**
 * Builds the asset for the active timeline container's orthophoto, XYZ or COG, with its tiles
 * placed relative to sceneOriginProject.
 */
export function createActiveOrthoTileAsset(params: {
  id: string;
  projection: string;
  /** Project coords at scene (0,0). */
  sceneOriginProject: { x: number; y: number };
}): OrthoTileAsset {
  const { id, projection, sceneOriginProject } = params;
  const orthoData = ProjectGeodataManager.instance().getActivOrthotileGeodata();
  if (!orthoData) {
    throw new Error('No active orthotile geodata.');
  }

  if (orthoData.format === 'COG') {
    return createCogOrthoAsset({
      id,
      source: orthoData.source,
      projectProjection: projection,
      sceneOriginProject,
    });
  }

  if (!orthoData.model?.levels?.length) {
    throw new Error('The active orthotile has no tile levels.');
  }
  const { model, store } = orthoData;
  const webMercatorToProject = proj4('EPSG:3857', projection);
  return new OrthoTileAsset({
    id,
    loader: new TileLoader({ store }),
    cache: new TileCache({ maxTextures: 512 }),
    keepRootsVisible: true,
    levels: model.levels,
    worldMapping: new FlatPlaneWorldMapping({
      tileBoundsToWorldQuad: makeTileBoundsToWorldQuad({
        webMercatorToProject,
        sceneOriginProject,
      }),
      colorSpace: SRGBColorSpace,
      anisotropy: 8,
      polygonOffset: { factor: -1, units: -1 },
      transparent: true,
      depthWrite: false,
      renderOrder: renderOrders.orthoPhoto,
    }),
  });
}

/**
 * Applies the orthophoto's blending and opacity from the view settings to the asset, now and
 * whenever they change. Returns a function that stops listening.
 */
export function bindOrthophotoSettings(asset: OrthoTileAsset): () => void {
  const apply = () => {
    const { blending, opacity } = ViewSettings.instance.orthophoto;
    asset.setMagFilter(blending === 'pixelated' ? NearestFilter : LinearFilter);
    asset.setOpacity(opacity);
  };
  apply();
  return ViewSettings.instance.subscribe(apply);
}
